import {AgentIcon, BookIcon, IdBadgeIcon, LibraryIcon, PlugIcon, ServerIcon} from '@primer/octicons-react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {advancedSearchPath, getRelativeHref, searchPath} from '@github-ui/paths'
import type React from 'react'
import {useCallback, useMemo} from 'react'

import type {AIResourceResult} from './AIResourceResults'
import {AIResourceSearchPage, type AIResourceSearchPageState} from './AIResourceSearchPage'
import type {ResourceTypeFilterProps} from './ResourceTypeFilter'
import type {
  AIResourceSearchCategory,
  AIResourceSearchError,
  AIResourceSearchKind,
  AIResourceSearchKindCount,
  AIResourceSearchPayload,
  AIResourceSearchPayloadInput,
  AIResourceSearchResult,
  AIResourceNoResultsProps,
  AIResourcePluginMarketplace,
} from './types'

export type AIResourceSearchProps = {
  counts: readonly AIResourceSearchKindCount[]
  isLoadingCounters: boolean
  navigateToSearch: (query: Record<string, string | null>) => void
  payload: AIResourceSearchPayloadInput | null
  query: string
  renderError: (error: AIResourceSearchError, index: number) => React.ReactNode
  renderLoadingResults: () => React.ReactNode
  renderNoResults: (props: AIResourceNoResultsProps) => React.ReactNode
  resultKind: AIResourceSearchKind
}

const resourceTypes = [
  {
    label: 'Skill',
    mediaType: 'application/agent-skills+md',
    mediaTypeAlias: 'application/ai-skill',
    qualifier: 'type:"Skill"',
    icon: BookIcon,
  },
  {
    label: 'MCP server',
    filterLabel: 'MCP Server',
    mediaType: 'application/mcp-server+json',
    mediaTypeAlias: 'application/mcp-server-card+json',
    qualifier: 'type:"MCP Server"',
    icon: ServerIcon,
  },
  {
    label: 'Agent',
    mediaType: 'application/agents+md',
    mediaTypeAlias: 'application/agent+json',
    icon: AgentIcon,
  },
  {
    label: 'Plugin',
    mediaType: 'application/agent-plugin+json',
    mediaTypeAliases: ['application/vnd.github.copilot-plugin', 'application/vnd.anthropic.claude-plugin+json'],
    qualifier: 'type:"Plugin"',
    icon: PlugIcon,
  },
  {
    label: 'A2A agent',
    mediaType: 'application/a2a-agent-card+json',
    icon: IdBadgeIcon,
  },
] as const

const clearedSearchParameters = {p: null, page_token: null, l: null, s: null, o: null}
const rawBase64UrlPattern = /^[A-Za-z0-9_-]+$/
const mcpServerNameMaxLength = 200
const mcpServerNamePattern = /^[a-zA-Z0-9](?:[a-zA-Z0-9.-]*[a-zA-Z0-9])?\/[a-zA-Z0-9](?:[a-zA-Z0-9._-]*[a-zA-Z0-9])?$/
const mcpServerMediaTypes: ReadonlySet<string> = new Set([
  'application/mcp-server+json',
  'application/mcp-server-card+json',
])
const skillMediaTypes: ReadonlySet<string> = new Set(['application/agent-skills+md', 'application/ai-skill'])
const pluginLabelsByMediaType: ReadonlyMap<string, string> = new Map([
  ['application/agent-plugin+json', 'Agent Plugin'],
  ['application/vnd.github.copilot-plugin', 'Copilot Plugin'],
  ['application/vnd.anthropic.claude-plugin+json', 'Claude Plugin'],
])
const rawGitHubPathSegmentPattern = /^[A-Za-z0-9._-]+$/
const gitHubRepositoryPattern = /^[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\/[A-Za-z0-9_.-]+$/
const pluginInstallNamePattern = /^[a-z][a-z0-9._-]*$/
const positiveIntegerPattern = /^[1-9][0-9]*$/
const schemaVersionPattern = /^[0-9]+\.[0-9]+\.[0-9]+$/
const sha256DigestPattern = /^sha256:[0-9a-f]{64}$/

type AgentFinderPageToken = {
  version: 2
  totalCount: number
  pageSize: number
  currentPage: number
  pageCount: number
  inputHash: string
  signature: string
  targetPage?: number
  [key: string]: unknown
}

type AIResourcePagination = {
  token: AgentFinderPageToken
  currentPage: number
  pageCount: number
  totalCount: number
}

export function AIResourceSearch({
  counts,
  isLoadingCounters,
  navigateToSearch,
  payload,
  query,
  renderError,
  renderLoadingResults,
  renderNoResults,
  resultKind,
}: AIResourceSearchProps) {
  const pagination = useMemo(() => getPagination(payload), [payload])
  const getPageHref = useCallback(
    (page: number) => {
      if (!pagination) return '#'

      return getRelativeHref(searchPath, undefined, {
        p: null,
        page_token: encodePageToken(pagination.token, page),
      })
    },
    [pagination],
  )
  const onPageChange = useCallback(
    (event: React.MouseEvent, page: number) => {
      if (!pagination || event.defaultPrevented || event.button !== 0) return
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- preserve native link-modifier behavior
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

      event.preventDefault()
      if (page === pagination.currentPage) return

      window.scrollTo(0, 0)
      navigateToSearch({p: null, page_token: encodePageToken(pagination.token, page)})
    },
    [navigateToSearch, pagination],
  )

  const categories: AIResourceSearchCategory[] = counts.map(count => ({
    label: capitalize(count.readableNamePlural),
    href: getRelativeHref(searchPath, undefined, {...clearedSearchParameters, q: query, type: count.name}),
    icon: count.icon,
    count: count.final && !count.error ? count.total : undefined,
    current: count.name === 'ai_resources',
  }))

  const filters: ResourceTypeFilterProps = {
    resourceTypes: resourceTypes
      .filter(resourceType => 'qualifier' in resourceType)
      .map(resourceType => ({
        label: 'filterLabel' in resourceType ? resourceType.filterLabel : resourceType.label,
        href: getFilterHref(query, resourceType.qualifier),
        icon: resourceType.icon,
      })),
    labelFilterHref: getFilterHref(query, 'label:'),
    advancedSearchHref: getRelativeHref(advancedSearchPath, undefined, {q: query}),
  }

  return (
    <AIResourceSearchPage
      categories={categories}
      elapsedMilliseconds={payload?.elapsed_millis}
      filters={filters}
      isLoadingCounters={isLoadingCounters}
      onPageChange={onPageChange}
      query={query}
      renderError={renderError}
      renderLoadingResults={renderLoadingResults}
      renderNoResults={renderNoResults}
      resultKind={resultKind}
      searchKinds={[...counts]}
      state={getState(payload, pagination, getPageHref)}
    />
  )
}

function getFilterHref(query: string, qualifier: string) {
  return getRelativeHref(searchPath, undefined, {
    ...clearedSearchParameters,
    q: `${query} ${qualifier}`.trim(),
    type: 'ai_resources',
  })
}

function getState(
  payload: AIResourceSearchPayloadInput | null,
  pagination: AIResourcePagination | null,
  getPageHref: (page: number) => string,
): AIResourceSearchPageState {
  if (!payload) return {status: 'loading'}
  if (payload.errors.length > 0) return {status: 'error', errors: payload.errors}
  if (!isAIResourceSearchPayload(payload)) return {status: 'empty'}

  const results = payload.results.flatMap(result => {
    const mappedResult = mapResult(result)
    return mappedResult ? [mappedResult] : []
  })
  if (results.length === 0 && (!pagination || pagination.totalCount === 0)) return {status: 'empty'}

  return {
    status: 'success',
    results,
    totalCount: pagination?.totalCount,
    currentPage: pagination?.currentPage,
    pageCount: pagination?.pageCount,
    getPageHref: pagination ? getPageHref : undefined,
  }
}

function getPagination(payload: AIResourceSearchPayloadInput | null): AIResourcePagination | null {
  if (!payload || !isAIResourceSearchPayload(payload) || !payload.page_token) return null

  const token = decodePageToken(payload.page_token)
  if (
    !token ||
    token.totalCount !== payload.result_count ||
    token.currentPage !== payload.page ||
    token.pageCount !== payload.page_count
  ) {
    return null
  }

  return {
    token,
    totalCount: token.totalCount,
    currentPage: token.currentPage,
    pageCount: token.pageCount,
  }
}

function isAIResourceSearchPayload(payload: AIResourceSearchPayloadInput): payload is AIResourceSearchPayload {
  return payload.type === 'ai_resources' && 'results' in payload && Array.isArray(payload.results)
}

function decodePageToken(value: string): AgentFinderPageToken | null {
  if (!rawBase64UrlPattern.test(value)) return null

  try {
    const padding = '='.repeat((4 - (value.length % 4)) % 4)
    const binary = globalThis.atob(value.replaceAll('-', '+').replaceAll('_', '/') + padding)
    const bytes = Uint8Array.from(binary, character => character.charCodeAt(0))
    const token: unknown = JSON.parse(new TextDecoder().decode(bytes))
    if (!isAgentFinderPageToken(token)) return null

    return token
  } catch {
    return null
  }
}

function isAgentFinderPageToken(value: unknown): value is AgentFinderPageToken {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false

  const token = value as Record<string, unknown>
  return (
    token.version === 2 &&
    isNonNegativeInteger(token.totalCount) &&
    isPositiveInteger(token.pageSize) &&
    isPositiveInteger(token.currentPage) &&
    isNonNegativeInteger(token.pageCount) &&
    typeof token.inputHash === 'string' &&
    token.inputHash.length > 0 &&
    typeof token.signature === 'string' &&
    token.signature.length > 0 &&
    (token.targetPage === undefined || isPositiveInteger(token.targetPage)) &&
    (token.pageCount === 0 ? token.currentPage === 1 : token.currentPage <= token.pageCount)
  )
}

function encodePageToken(token: AgentFinderPageToken, targetPage: number) {
  const bytes = new TextEncoder().encode(JSON.stringify({...token, targetPage}))
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)

  return globalThis.btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '')
}

function isPositiveInteger(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) > 0
}

function isNonNegativeInteger(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) >= 0
}

function mapResult(result: AIResourceSearchResult): AIResourceResult | null {
  const href = result.href
  if (!isSafeUrl(href)) return null

  const publisher = result.publisher
  const resourceType = resourceTypes.find(
    candidate =>
      candidate.mediaType === result.resource_type ||
      ('mediaTypeAlias' in candidate && candidate.mediaTypeAlias === result.resource_type) ||
      ('mediaTypeAliases' in candidate && candidate.mediaTypeAliases.some(alias => alias === result.resource_type)),
  )
  return {
    id: result.id,
    href,
    name: result.name,
    iconUrl: result.icon_url ?? undefined,
    fallbackIconUrl: result.fallback_icon_url ?? undefined,
    description: result.description ?? undefined,
    resourceType: pluginLabelsByMediaType.get(result.resource_type) ?? resourceType?.label ?? 'AI resource',
    resourceTypeIcon: resourceType?.icon ?? LibraryIcon,
    labels: result.labels,
    publisher: publisher && isSafeUrl(publisher.href) ? publisher : undefined,
    installAction: getInstallAction(result),
    connectorIds: result.metadata?.github?.connectorIds,
  }
}

function getInstallAction(result: AIResourceSearchResult) {
  if (isFeatureEnabled('ai_resource_search_deeplinks')) {
    const deeplinkAction = getDeeplinkInstallAction(result)
    if (deeplinkAction) return deeplinkAction
  }

  const install = result.install
  if (mcpServerMediaTypes.has(result.resource_type) && install?.type === 'mcp_server') {
    if (
      typeof install.server_name !== 'string' ||
      typeof install.url !== 'string' ||
      install.server_name.length > mcpServerNameMaxLength ||
      !mcpServerNamePattern.test(install.server_name) ||
      !isMcpRegistryUrl(install.url)
    ) {
      return undefined
    }

    const serverName = install.server_name
    return {
      type: 'mcp_server' as const,
      command: `/mcp search ${serverName}`,
      target: {
        name: serverName,
        full_name: serverName,
        api_name: serverName,
      },
    }
  }

  if (skillMediaTypes.has(result.resource_type) && install?.type === 'skill') {
    const skill = parseRawGitHubSkillUrl(install.url)
    if (!skill || typeof install.has_companion_files !== 'boolean') return undefined

    const hasCompanionFiles = install.has_companion_files
    if (!hasCompanionFiles) {
      return {
        type: 'skill' as const,
        command: `copilot skill add ${install.url}`,
        hasCompanionFiles,
      }
    }

    const repositoryUrl = `https://github.com/${skill.owner}/${skill.repository}.git`
    const skillDirectory = [skill.repository, ...skill.filePath.slice(0, -1)].join('/')
    return {
      type: 'skill' as const,
      command: [`git clone ${repositoryUrl} &&`, `copilot skill add ./${skillDirectory}`].join('\n'),
      // Complete installs intentionally use the repository's current default branch.
      hasCompanionFiles,
    }
  }

  if (pluginLabelsByMediaType.has(result.resource_type) && install?.type === 'plugin') {
    if (
      !isGitHubRepository(install.repository) ||
      typeof install.path !== 'string' ||
      (install.path !== '' && !install.path.split('/').every(isSafePathSegment))
    ) {
      return undefined
    }

    return {
      type: 'plugin' as const,
      command: `copilot plugin install ${install.repository}${install.path ? `:${install.path}` : ''}`,
      marketplaces: Array.isArray(install.marketplaces) ? install.marketplaces.filter(isPluginMarketplace) : [],
    }
  }

  return undefined
}

function getDeeplinkInstallAction(resource: AIResourceSearchResult) {
  if (!pluginLabelsByMediaType.has(resource.resource_type)) return undefined

  const target = parseInstallDeeplink(resource.install_deeplink)
  if (!target) return undefined

  return {type: 'deeplink' as const, command: `copilot plugin install "${target}"`, target}
}

function parseInstallDeeplink(value: unknown) {
  if (typeof value !== 'string') return undefined

  let url: URL
  try {
    // eslint-disable-next-line no-restricted-syntax -- Only absolute targets are valid, including during SSR.
    url = new URL(value)
  } catch {
    return undefined
  }

  const resource = url.searchParams.get('resource')
  const revision = url.searchParams.get('revision')
  const schemaVersion = url.searchParams.get('schemaVersion')
  const descriptorDigest = url.searchParams.get('descriptorDigest')
  const bundleDigest = url.searchParams.get('bundleDigest')
  if (
    url.protocol !== 'ghapp:' ||
    url.hostname !== 'air' ||
    url.pathname !== '/install' ||
    url.username !== '' ||
    url.password !== '' ||
    url.hash !== '' ||
    [...url.searchParams.keys()].length !== 5 ||
    !resource?.match(positiveIntegerPattern) ||
    !revision?.match(positiveIntegerPattern) ||
    !schemaVersion?.match(schemaVersionPattern) ||
    !descriptorDigest?.match(sha256DigestPattern) ||
    !bundleDigest?.match(sha256DigestPattern)
  ) {
    return undefined
  }

  const canonicalTarget =
    `ghapp://air/install?resource=${resource}&revision=${revision}&schemaVersion=${schemaVersion}` +
    `&descriptorDigest=${descriptorDigest}&bundleDigest=${bundleDigest}`
  return value === canonicalTarget ? canonicalTarget : undefined
}

function isSafePathSegment(segment: string) {
  return segment !== '.' && segment !== '..' && rawGitHubPathSegmentPattern.test(segment)
}

function isGitHubRepository(value: unknown): value is string {
  if (typeof value !== 'string' || !gitHubRepositoryPattern.test(value)) return false

  const repository = value.split('/')[1]
  return repository !== '.' && repository !== '..'
}

function isPluginMarketplace(value: unknown): value is AIResourcePluginMarketplace {
  if (!value || typeof value !== 'object') return false

  return (
    'entry' in value &&
    typeof value.entry === 'string' &&
    pluginInstallNamePattern.test(value.entry) &&
    'name' in value &&
    typeof value.name === 'string' &&
    pluginInstallNamePattern.test(value.name) &&
    'source' in value &&
    isGitHubRepository(value.source)
  )
}

function isMcpRegistryUrl(value: string) {
  try {
    // A base would allow relative values, and browser globals are unavailable during SSR.
    // eslint-disable-next-line no-restricted-syntax
    const url = new URL(value)
    return url.protocol === 'https:' && url.hostname === 'api.mcp.github.com'
  } catch {
    return false
  }
}

function parseRawGitHubSkillUrl(value: string) {
  try {
    // A base would allow relative values, and browser globals are unavailable during SSR.
    // eslint-disable-next-line no-restricted-syntax
    const url = new URL(value)
    const pathSegments = url.pathname.slice(1).split('/')
    const valid =
      url.protocol === 'https:' &&
      url.hostname === 'raw.githubusercontent.com' &&
      url.port === '' &&
      url.username === '' &&
      url.password === '' &&
      url.search === '' &&
      url.hash === '' &&
      url.toString() === value &&
      pathSegments.length >= 4 &&
      pathSegments.every(
        segment => segment !== '' && segment !== '.' && segment !== '..' && rawGitHubPathSegmentPattern.test(segment),
      )
    if (!valid) return undefined

    const [owner, repository, sourceRevision, ...filePath] = pathSegments
    const fileName = filePath.at(-1)
    if (!owner || !repository || !sourceRevision || fileName !== 'SKILL.md') return undefined

    return {owner, repository, filePath}
  } catch {
    return undefined
  }
}

function isSafeUrl(value: string | null): value is string {
  if (!value || !URL.canParse(value)) return false

  try {
    // A base would allow relative values, and browser globals are unavailable during SSR.
    // eslint-disable-next-line no-restricted-syntax
    const {protocol} = new URL(value)
    return protocol === 'https:' || protocol === 'http:'
  } catch {
    return false
  }
}

function capitalize(value: string) {
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`
}
